export class GridConfig {
    constructor(
        public hideFilters: boolean,
        public isInsidePanel: boolean,
        public defaultSortColumn: string,
        public rowKey: string,
        public statusValue: number,
        public enableRowSelection: boolean,
        public countByStatus: CountByStatus[],
        public suppressCellSelection: boolean,
        public suppressRowClickSelection: boolean,
        public isCheckAllAllowed: boolean,
        public gridTitle: string,
        public defaultSortOrder: string,
        public rowHeight: number,
        public noDataMessage: string,
        public headerHeight: number = 0,
        public rowClassRules: any,
        public isHoverRowColumnHighlight: boolean,
        public suppressRowHoverHighlight: boolean,
        public searchBarClass: any,
        public gridWrapperClass: string,
        public columnHoverHighlight: boolean,
        public gridHeaderMessage:string = "",
        public dataFilterValue: number = 0,
        public dataFilterTitle: string = "",
        public gridFilterPanelConfig: GridFilterPanelConfig = null,
        public gridTabConfig: GridTabConfig = null,
        public showFilter: boolean = true,
        public showSearch: boolean = true,
        public useClientSideRowModel: boolean = false,
        public suppressAllFilterOption: boolean = false,
        /**
         * Opt-in date picker in the toolbar's LEFT slot (UC7 Screen 73 "Start Date"). Defaults to
         * false so every existing consumer's toolbar is unchanged. The grid does NOT filter on it —
         * it only emits `dateFilterChanged`; the host re-fetches.
         */
        public showDateFilter: boolean = false,
        /** Label above the date picker. A translator key is NOT resolved here — pass resolved text. */
        public dateFilterLabel: string = "",
        /** Latest selectable date, `yyyy-MM-dd`. Empty = no upper bound. */
        public dateFilterMax: string = "",
        /**
         * Opt in to the `row-highlight` class on rows carrying `isRowHighlighted` (the
         * highlight-then-fade UX after a save). Off by default, so `rowClassRules` is handed to
         * ag-grid exactly as the host wrote it and no consumer gains a rule it did not define.
         */
        public enableRowHighlight: boolean = false,
        /**
         * Opt in to check-all acting on the SEARCH-MATCHED rows instead of every loaded row.
         *
         * Off by default because it changes which rows a tick selects. Note the default is also the
         * buggy path: with a search term active the original code selected nothing at all. New
         * screens should set this true.
         */
        public checkAllRespectsSearch: boolean = false,
    ) {
    }

    public gridTitleClass: string = '';
    public gridTitleHyperlink: string = '';
    public cacheBlockSize: number = 100;
    public showTagTextValue: boolean = false;

    /**
     * Opt in to a hand pointer over the whole row, for a grid where clicking ANY cell opens that
     * item.
     *
     * Off by default, and it must stay a decision the screen makes: on a grid whose rows only
     * respond to a chevron or an action icon, a pointer across the row would promise a click that
     * does nothing. The pinned totals row never gets it - see `_ag-grid.scss`.
     */
    public rowClickOpensItem: boolean = false;
}

export class CountByStatus {
    constructor(
        public className: string,
        public label: string,
        public count: number,
        public isClickEvent: boolean,
        public value: number        
    ) {        
    }
}
export class GridFilterPanelConfig{
    panelHeader: string;
    panelHeaderCount: number;
    sections: GridFilterPanelSection[];
}
export class GridFilterPanelSection {
    sectionHeader: string;
    sectionHeaderCount: number;
    sectionOptions: GridFilterPanelSectionOption[];
}
export class GridFilterPanelSectionOption {
   optionName: string;
   optionValue: number;
   optionCount: number;
   optionIcon: string;
}

export class GridTabConfig {
    tabs: GridTab[] = [];
    showWhenStatusValue: number;
}

export class GridTab {
    label: string;
    count: number;
    statusValue: number;
    isActive: boolean;
}
/**
 * A compact sub-tab in the shared `<grid>` toolbar's LEFT slot, e.g. "Matched with receipts (2)".
 *
 * Deliberately neutral: `<grid>` has 11 consumers outside the claimant feature, so it must not
 * import `SegmentCard` from `claimant-unified-grid`. `SegmentCard` is structurally compatible with
 * this, so a `SegmentCard[]` binds to `[segmentTabs]` with no cast or mapping.
 *
 * NOT the same thing as `GridTab`/`GridTabConfig` below — those are status-keyed and read only by
 * the legacy `<app-grid>`.
 */
export interface GridSegmentTab {
    value: any;
    label: string;
    count?: number | null;
}

/**
 * A button in the shared `<grid>` toolbar's bulk-action row (shown only while rows are selected).
 *
 * Label-only by design — no icon field. An icon widens the button enough that Delete/Move to no
 * longer line up with the label-only Deselect All beside them, so the row is kept uniform.
 *
 * `color` is a `ButtonConfig` colour: `primary`, `danger-tertiary`, `white-outline`, `success`, …
 */
/**
 * A dropdown shown in the bulk toolbar, for actions that need a value as well as a click - "apply THIS
 * tax rate to the selected rows". Bound with `[bulkSelect]` plus `[(bulkSelectValue)]`; the host reads
 * its own bound value when `bulkActionClicked` fires.
 *
 * Kept separate from the grid's filter dropdown on purpose: the filter changes which rows are shown and
 * acts immediately, while this one stages a value until an action button is pressed. The grid shows only
 * one of the two at a time, since the filter is hidden while rows are selected.
 */
export interface GridBulkSelect {
    /** Already-translated caption, e.g. "Setup tax rate for selected categories". */
    label?: string;
    placeholder?: string;
    /** Items for the dropdown. */
    options: any[];
    /** Property to show; defaults to 'value'. */
    bindLabel?: string;
    /** Property to emit; defaults to 'id'. */
    bindValue?: string;
    /** When true, the bulk action buttons stay disabled until something is chosen. */
    required?: boolean;
}

export interface GridBulkActionButton {
    key: string;
    label: string;
    color?: string;
    disabled?: boolean;
}

